export const FAVOURITES_KEY = 'mayfair:favourites:v1';
export function normaliseFavourites(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter(handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)))];
}
export function createFavouriteStore(storage) {
  let handles = [];
  let persistent = Boolean(storage);
  function refresh() {
    if (persistent) {
      try {
        const raw = storage.getItem(FAVOURITES_KEY);
        try { handles = normaliseFavourites(JSON.parse(raw || '[]')); }
        catch { handles = []; }
      } catch { persistent = false; }
    }
    return [...handles];
  }
  function set(handle, saved) {
    if (!normaliseFavourites([handle]).length) return false;
    refresh();
    const next = handles.filter(item => item !== handle);
    if (saved) next.unshift(handle);
    if (persistent) {
      try { storage.setItem(FAVOURITES_KEY, JSON.stringify(next)); }
      catch { return false; }
    }
    handles = next;
    return true;
  }
  refresh();
  return { refresh, set, get persistent() { return persistent; } };
}

if (typeof document !== 'undefined') initialiseFavourites();
export function initialiseFavourites() {
  const configElement = document.querySelector('[data-favourites-config]');
  if (!configElement) return;
  const copy = JSON.parse(configElement.textContent);
  let storage;
  try { storage = window.localStorage; } catch { storage = null; }
  const store = createFavouriteStore(storage);
  const page = document.querySelector('[data-favourites-page]');
  const grid = page?.querySelector('[data-favourites-grid]');
  const summary = page?.querySelector('[data-favourites-summary]');
  const toast = document.querySelector('[data-favourites-toast]');
  const undo = document.querySelector('[data-favourites-undo]');
  const slots = new Map();
  const loads = new WeakMap();
  let undoChange;
  let activeLoads = 0;
  const queue = [];
  function announce(message, change) {
    if (!toast) return;
    toast.querySelector('[data-favourites-announcement]').textContent = message;
    toast.hidden = false;
    undoChange = change;
    if (undo) undo.hidden = !change;
  }
  function updateButtons(root = document) {
    const saved = new Set(store.refresh());
    root.querySelectorAll('[data-favourite-toggle]').forEach(button => {
      const selected = saved.has(button.dataset.favouriteToggle);
      const label = selected ? copy.remove : copy.save;
      button.hidden = false;
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', `${label}: ${button.dataset.favouriteTitle}`);
      button.title = label;
      const text = button.querySelector('[data-favourite-label]');
      if (text) text.textContent = selected ? copy.saved : copy.save;
    });
    document.querySelectorAll('[data-favourites-count]').forEach(count => {
      count.textContent = saved.size;
      count.hidden = saved.size === 0;
    });
    document.querySelectorAll('[data-favourites-link]').forEach(link => {
      link.setAttribute('aria-label', `${copy.name} (${saved.size})`);
      link.setAttribute('title', `${copy.name} (${saved.size})`);
      if (page) link.setAttribute('aria-current', 'page');
    });
  }
  function updateSummary() {
    if (!page) return;
    const count = store.refresh().length;
    summary.textContent = count === 1 ? copy.one : copy.many.replace('{count}', count);
    page.querySelector('[data-favourites-empty]').hidden = count !== 0;
    grid.hidden = count === 0;
    grid.setAttribute('aria-busy', String([...slots.values()].some(slot => slot.dataset.loading === 'true')));
  }
  function enqueue(job) {
    queue.push(job);
    drain();
  }
  function drain() {
    while (activeLoads < 3 && queue.length) {
      activeLoads++;
      queue.shift()().finally(() => { activeLoads--; drain(); });
    }
  }
  function fallback(slot, handle, unavailable) {
    const title = document.createElement('h3');
    title.textContent = handle.replaceAll('-', ' ');
    const message = document.createElement('p');
    message.textContent = unavailable ? copy.unavailable : copy.loadError;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'text-link';
    remove.textContent = copy.removeShort;
    remove.dataset.favouriteToggle = handle;
    remove.dataset.favouriteTitle = title.textContent;
    remove.setAttribute('aria-pressed', 'true');
    slot.className = 'favourite-unavailable';
    slot.replaceChildren(title, message, remove);
    if (!unavailable) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'text-link';
      retry.textContent = copy.retry;
      retry.addEventListener('click', () => scheduleCard(slot, handle));
      slot.append(retry);
    }
    updateButtons(slot);
  }
  function scheduleCard(slot, handle) {
    loads.get(slot)?.abort();
    const controller = new AbortController();
    loads.set(slot, controller);
    const isCurrent = () => slots.get(handle) === slot && loads.get(slot) === controller;
    slot.dataset.loading = 'true';
    slot.className = 'favourite-loading';
    const loading = document.createElement('p');
    loading.textContent = copy.loading;
    slot.replaceChildren(loading);
    updateSummary();
    enqueue(async () => {
      if (!isCurrent() || controller.signal.aborted) return;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const url = new URL(`${copy.root}products/${encodeURIComponent(handle)}`, window.location.origin);
        url.searchParams.set('section_id', 'favourite-card');
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, {signal: controller.signal});
        if (!isCurrent()) return;
        if (!response.ok) {
          fallback(slot, handle, response.status === 404 || response.status === 410);
          return;
        }
        const body = await response.text();
        if (!isCurrent()) return;
        const parsed = new DOMParser().parseFromString(body, 'text/html');
        const card = parsed.querySelector(`[data-favourite-card="${handle}"] .print-card`);
        if (!card) { fallback(slot, handle, true); return; }
        slot.className = 'favourite-item';
        slot.replaceChildren(document.importNode(card, true));
        updateButtons(slot);
      } catch { if (isCurrent()) fallback(slot, handle, false); }
      finally {
        clearTimeout(timeout);
        if (loads.get(slot) === controller) {
          loads.delete(slot);
          slot.dataset.loading = 'false';
          updateSummary();
        }
      }
    });
  }
  function renderPage() {
    if (!page) return;
    const handles = store.refresh();
    const saved = new Set(handles);
    for (const [handle, slot] of slots) {
      if (!saved.has(handle)) { loads.get(slot)?.abort(); slot.remove(); slots.delete(handle); }
    }
    for (const handle of handles) {
      if (!slots.has(handle)) {
        const slot = document.createElement('div');
        slots.set(handle, slot);
        grid.append(slot);
        scheduleCard(slot, handle);
      }
    }
    handles.forEach(handle => grid.append(slots.get(handle)));
    updateSummary();
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-favourite-toggle]');
    if (!button) return;
    const handle = button.dataset.favouriteToggle;
    const wasSaved = store.refresh().includes(handle);
    const pageButtons = grid ? [...grid.querySelectorAll('[data-favourite-toggle]')] : [];
    const focusIndex = pageButtons.indexOf(button);
    if (!store.set(handle, !wasSaved)) { announce(copy.storageError); return; }
    updateButtons();
    renderPage();
    announce(`${wasSaved ? copy.removed : copy.saved}${store.persistent ? '' : `. ${copy.storageUnavailable}`}`, {handle, saved: wasSaved});
    if (focusIndex >= 0 && wasSaved) {
      const next = grid.querySelectorAll('[data-favourite-toggle]');
      (next[Math.min(focusIndex, next.length - 1)] || page.querySelector('[data-favourites-heading]')).focus();
    }
  });
  undo?.addEventListener('click', () => {
    if (!undoChange) return;
    const {handle, saved} = undoChange;
    if (!store.set(handle, saved)) { announce(copy.storageError); return; }
    updateButtons();
    renderPage();
    announce(saved ? copy.saved : copy.removed);
    document.querySelector('[data-favourites-link]')?.focus();
  });
  document.querySelector('[data-favourites-dismiss]')?.addEventListener('click', () => {
    toast.hidden = true;
    document.querySelector('[data-favourites-link]')?.focus();
  });
  window.addEventListener('storage', event => {
    if (event.key === FAVOURITES_KEY || event.key === null) { updateButtons(); renderPage(); }
  });
  window.addEventListener('pageshow', () => { updateButtons(); renderPage(); });
  document.addEventListener('shopify:section:load', () => updateButtons());
  updateButtons();
  renderPage();
  if (!store.persistent && page) announce(copy.storageUnavailable);
  const artistToggle = document.querySelector('.collection-artist-toggle');
  if (artistToggle) {
    artistToggle.hidden = false;
    artistToggle.setAttribute('aria-expanded', String(!window.matchMedia('(max-width:700px)').matches));
    artistToggle.addEventListener('click', () => artistToggle.setAttribute('aria-expanded', String(artistToggle.getAttribute('aria-expanded') !== 'true')));
  }
  document.querySelector('[data-collection-sort]')?.addEventListener('change', event => event.target.form.requestSubmit());
  document.body.classList.add('favourites-ready');
}
